import { calculateCouponDiscount, describeCoupon, isShippingCouponType } from "@/utils/coupons";
import { useContractedItems } from '@/hooks/useContractedItems';
import { ContractedItemDetails } from '@/components/checkout/ContractedItemDetails';
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ShoppingCart, Trash2, MapPin, Users, Calendar as CalendarIcon, DollarSign, Loader2, ChevronRight, CreditCard, Ban, Clock, Pencil, Tag, X, Share2, Truck, PackageCheck, MessageCircle, Copy, AlertCircle, ChevronDown, Minus, Plus } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { formatEventDuration, parseDurationToHours } from "@/utils/formatEventDuration";
import { resolveFreightContextFromCart, fetchCoverageRowsForContext } from '@/utils/deliveryContext';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cartHasChoiceCategories, getQuoteOnlySelected, setQuoteOnlySelected, SHARED_QUOTE_LOADED_EVENT } from "@/utils/quoteVisibility";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CartItem, removeCartItem, clearCart, getCartItems, getCartItemOptionalCost, enrichCartItemsWithOptionalCosts, resolveItemEventType, getItemEventTypeMultiplier, EVENT_TYPE_CHANGED } from "@/utils/cartStorage";
import { calculateAdditionsCost, normalizeAdditions } from "@/types/menuAdditions";
import { calculateProductPriceWithRanges, getAdjustedPricePerPerson } from "@/components/event-simulator/utils/pricingRangeUtils";
import { applyHourExtension, parseSpecialPricingTiers } from "@/components/event-simulator/utils/pricingAdjustmentUtils";
import { getRangeGuestCount, getSpecialExtraForProduct } from "@/utils/specialExtra";
import { buildSpecialGuestsDisplay, getSpecialGuestsCount, formatSpecialGuestsSuffix } from "@/utils/specialGuests";
import { saveCacheData, loadCacheData, clearCacheData } from "@/utils/sessionStorage";
import { formatDateForStorage, normalizeDate, parseDateFromStorage } from "@/utils/dateUtils";
import { buildStoreReturnUrl } from "@/utils/storeReturnUrl";
import { NEW_QUOTE_SESSION_KEYS, buildNewQuoteUrl } from "@/utils/newQuoteReset";
import { formatCepForInput } from "@/utils/inputFormatters";
import { cn } from "@/lib/utils";
import { getSupplierColors, loadSupplierCustomization } from "@/utils/supplierCustomizationCache";
import { useSupplierAvailability } from "@/hooks/useSupplierAvailability";
import { useDeliveryValidation } from "@/hooks/useDeliveryValidation";
import { normalizeCEP } from "@/utils/cepUtils";
import type { CepRange } from "@/utils/cepUtils";
import { normalizeSupplierPaymentMethods, shouldOfferDeferredPix } from "@/utils/paymentMethods";
import { CartPaymentOptions } from "./CartPaymentOptions";
import { cartFreightExtra, computeDeliveryFee, freightExtraTouched, normalizeFreightExtra, parseFreightExtra, readSharedFreightExtra, withFreightExtra } from "@/utils/freightExtra";
import { fetchTollQuote } from "@/utils/tollService";
import { QuoteSendFormatModal } from './QuoteSendFormatModal';
import type { QuoteSendFormat } from '@/utils/quoteText';

interface CartSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cartItems: CartItem[];
  setCartItems: (items: CartItem[]) => void;
  onCheckout: () => void;
  isLoading: boolean;
  supplierSlug?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  onSyncCart?: (items: CartItem[], financialSummary?: { subtotal: number; freight: number; total: number; discount_code?: string; discount_value?: number }) => void;
  onRemoveItem?: (productId: string) => void;
  isQuoteMode?: boolean;
  /** Retorna true quando a proposta foi de fato compartilhada (link gerado e enviado/copiado). */
  onShareQuote?: (action?: 'whatsapp' | 'copy', format?: QuoteSendFormat) => Promise<void | boolean>;
  /** Rótulo customizado do campo de convidados (definido pelo fornecedor) */
  guestsLabel?: string | null;
  /** Pagamento pós-evento: prazo (30/60/90) escolhido pelo fornecedor pra ESTA proposta (mode=orcamento). */
  deferredPixDays?: number | null;
  onDeferredPixDaysChange?: (days: number | null) => void;
}

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CartSheet = ({
  open,
  onOpenChange,
  cartItems,
  setCartItems,
  onCheckout,
  isLoading,
  supplierSlug,
  guestsLabel,
  buttonColor,
  buttonTextColor,
  onSyncCart,
  onRemoveItem,
  isQuoteMode,
  onShareQuote,
  deferredPixDays,
  onDeferredPixDaysChange,
}: CartSheetProps) => {
  const navigate = useNavigate();
  // No celular o carrinho sobe como uma folha de baixo (estilo iFood); no desktop continua lateral.
  const isMobileSheet = useMediaQuery("(max-width: 767px)");

  // Limites de quantidade definidos pelo fornecedor em cada item do cardápio de seleção (min/max).
  const [qtyLimits, setQtyLimits] = useState<Record<string, { min: number; max: number }>>({});
  useEffect(() => {
    if (!open) return;
    const ids = Array.from(new Set(
      cartItems.flatMap((i: any) => (i?.isSelectableMenu && i?.selectedMenuItems ? Object.keys(i.selectedMenuItems) : []))
    )).filter((id) => !(id in qtyLimits));
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase as any).from('supplier_menu').select('id, min_qtde, max_qtde').in('id', ids);
        if (cancelled || !Array.isArray(data)) return;
        setQtyLimits((prev) => {
          const next = { ...prev };
          data.forEach((row: any) => {
            const min = parseInt(String(row.min_qtde ?? '').replace(/\D/g, ''), 10);
            const max = parseInt(String(row.max_qtde ?? '').replace(/\D/g, ''), 10);
            next[row.id] = { min: Number.isFinite(min) && min >= 1 ? min : 1, max: Number.isFinite(max) && max >= 1 ? max : 999 };
          });
          return next;
        });
      } catch { /* sem limites: usa 1..999 */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cartItems]);
  // Rastreia qual dos dois botões (WhatsApp/Copiar) está em andamento --
  // um boolean único fazia o "Gerando link..." aparecer no botão do
  // WhatsApp mesmo quando quem foi clicado era "Copiar Proposta".
  const [sharingAction, setSharingAction] = useState<'whatsapp' | 'copy' | null>(null);
  const [showSendFormat, setShowSendFormat] = useState(false);
  const sendInFlight = useRef(false);

  // Depois de compartilhar uma proposta, pergunta se o fornecedor quer começar outra.
  const [showNewQuotePrompt, setShowNewQuotePrompt] = useState(false);
  const startNewQuote = () => {
    clearCart();
    NEW_QUOTE_SESSION_KEYS.forEach((key) => {
      try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    });
    try {
      sessionStorage.setItem('ameup_quote_mode', 'true');
      if (supplierSlug) sessionStorage.removeItem(`store_filters_${supplierSlug}`);
    } catch { /* ignore */ }
    clearCacheData(); // cache da simulação (CEP, data e convidados do simulador)
    // URL limpa: buildStoreReturnUrl devolvia cep, data, convidados, ref e pid da proposta anterior que estavam na URL atual, e o simulador
    // da proposta nova abria preenchido com os dados da anterior (e podia reabrir a mesma proposta).
    window.location.href = buildNewQuoteUrl(supplierSlug);
  };
  const isSharing = sharingAction !== null;
  // Proposta: mostrar ao cliente só os itens escolhidos (+ adicionais com custo). Gravado junto com o resumo no próximo envio.
  const [onlySelected, setOnlySelected] = useState<boolean>(() => getQuoteOnlySelected());
  // Cupom na proposta fica recolhido atrás de "Adicionar cupom" (menos poluição no resumo).
  const [showCouponField, setShowCouponField] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const supplierColors = getSupplierColors();
  const themeColor = buttonColor || supplierColors.buttonColor;
  const themeTextColor = buttonTextColor || supplierColors.buttonTextColor;
  const themeIconColor = supplierColors.iconColor;
  const resolvedSupplierIdFromCustomization = loadSupplierCustomization()?.supplierId || cartItems[0]?.supplierId || null;
  const [resolvedSupplierId, setResolvedSupplierId] = useState<string | null>(resolvedSupplierIdFromCustomization);

  // Update resolvedSupplierId when cartItems change
  useEffect(() => {
    const idFromCart = cartItems[0]?.supplierId;
    if (idFromCart && idFromCart !== resolvedSupplierId) {
      setResolvedSupplierId(idFromCart);
    }
  }, [cartItems]);
  /**
   * Pedido só de encomenda (itens de seleção): a agenda válida é a de entregas
   * (`Opening_days_delivery` / `delivery_hours`), não a de eventos.
   */
  const isOrderOnlyCart = cartItems.length > 0 && cartItems.every((i: any) => !!i?.isSelectableMenu);
  const { isDateDisabled } = useSupplierAvailability(
    resolvedSupplierId,
    isOrderOnlyCart ? 'selecao' : undefined,
    { eventType: (cartItems[0] as any)?.eventType, guests: (cartItems[0] as any)?.guests },
  );
  const { validationResult, validateDeliveryWithRanges, clearValidation } = useDeliveryValidation();
  const [supplierRanges, setSupplierRanges] = useState<CepRange[]>([]);
  const [supplierPaymentInfo, setSupplierPaymentInfo] = useState<{ methods: string | null; installments: string | null; pixDiscountEnabled: boolean; pixDiscountPercent: number; deferredPixEnabled: boolean; deferredPixDaysOptions: number[] } | null>(null);
  const [supplierCancellationPolicy, setSupplierCancellationPolicy] = useState<any>(null);
  const deliveryFetchRef = useRef<string>('');
  const [supplierAddress, setSupplierAddress] = useState<{
    address_name?: string | null;
    address_number?: string | null;
    address_complement?: string | null;
    city?: string | null;
    state?: string | null;
    address_code?: string | null;
  } | null>(null);

  // Resolve supplier ID from slug or cart items if not cached
  useEffect(() => {
    if (resolvedSupplierId) return;
    const slug = supplierSlug || cartItems[0]?.supplierSlug;
    const supplierId = cartItems[0]?.supplierId;
    
    if (supplierId) {
      setResolvedSupplierId(supplierId);
      return;
    }
    
    if (!slug) return;
    (async () => {
      const { data } = await (supabase as any).from('suppliers_public').select('id').eq('slug', slug).maybeSingle();
      if (data?.id) setResolvedSupplierId(data.id);
    })();
  }, [resolvedSupplierId, supplierSlug, cartItems]);

  // Encomendas = todos os itens do carrinho vêm de cardápios selecionáveis.
  // A marcação salva no item pode faltar (carrinhos antigos, links). Confirmamos
  // pelo tipo real do cardápio no banco para nunca usar áreas de Eventos numa encomenda.
  const [dbMenuTypes, setDbMenuTypes] = useState<Record<string, string | null>>({});
  const cartProductIdsKey = Array.from(new Set(cartItems.map((i: any) => i?.productId).filter(Boolean))).sort().join(',');
  useEffect(() => {
    if (!cartProductIdsKey) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from('supplier_menu')
        .select('id, menu_type')
        .in('id', cartProductIdsKey.split(','));
      if (cancelled || !data) return;
      const map: Record<string, string | null> = {};
      data.forEach((row: any) => { map[row.id] = row.menu_type ?? null; });
      setDbMenuTypes(map);
    })();
    return () => { cancelled = true; };
  }, [cartProductIdsKey]);

  const cartPaymentContext = resolveFreightContextFromCart(
    cartItems.map((i: any) => ({
      isSelectableMenu: i?.productId && i.productId in dbMenuTypes
        ? dbMenuTypes[i.productId] === 'selecao' || (!!i.isSelectableMenu && !dbMenuTypes[i.productId])
        : !!i?.isSelectableMenu,
    })),
  );
  const cartFreightContext = cartPaymentContext;

  // Load supplier coverage areas
  useEffect(() => {
    setSupplierPaymentInfo(null);
    if (!resolvedSupplierId) return;
    // Troca de contexto: descarta áreas/validação anteriores para não exibir frete de outro tipo.
    setSupplierRanges([]);
    clearValidation();
    (async () => {
      const data = await fetchCoverageRowsForContext(resolvedSupplierId, cartFreightContext);
      if (data) {
        setSupplierRanges(data.map((row: any): CepRange => ({
          id: row.id,
          region: row.region_name || '',
          type: Array.isArray(row.celulas) && row.celulas.length > 0
            ? 'cells'
            : ((row.area_type as 'cep' | 'circle') || 'cep'),
          start: row.cep_start || undefined,
          end: row.cep_end || undefined,
          center: row.latitude != null && row.longitude != null
            ? [Number(row.latitude), Number(row.longitude)]
            : undefined,
          radiusMeters: row.radius_meters ?? undefined,
          deliveryFee: row.delivery_fee != null ? Number(row.delivery_fee) : 0,
          freeDelivery: row.free_delivery ?? false,
          isDisabled: row.is_disabled ?? false,
          deliveryTimeMinutes: row.delivery_time_minutes ?? 30,
          eventSizeSurchargeEnabled: row.event_size_surcharge_enabled ?? false,
          eventSizeSurcharges: Array.isArray(row.event_size_surcharges)
            ? row.event_size_surcharges.map((s: any) => ({
                minGuests: s.min_guests,
                maxGuests: s.max_guests,
                surcharge: s.surcharge,
              }))
            : [],
          context: (row.context as any) || 'event',
          perKmEnabled: row.per_km_enabled ?? false,
          perKmRate: row.per_km_rate != null ? Number(row.per_km_rate) : 0,
          perKmBaseFee: row.per_km_base_fee != null ? Number(row.per_km_base_fee) : 0,
          perKmMinFee: row.per_km_min_fee != null ? Number(row.per_km_min_fee) : 0,
          cells: Array.isArray(row.celulas) ? row.celulas : [],
          distanceRangesEnabled: row.distance_ranges_enabled ?? false,
          distanceRanges: Array.isArray(row.distance_ranges)
            ? row.distance_ranges.map((r: any) => ({
                minKm: Number(r.minKm) || 0,
                maxKm: Number(r.maxKm) || 0,
                deliveryFee: Number(r.deliveryFee) || 0,
                freeDelivery: r.freeDelivery ?? false,
              }))
            : [],
        })));
      }
    })();
  }, [resolvedSupplierId, cartFreightContext]);

  // Load supplier payment methods and cancellation policy
  useEffect(() => {
    if (!resolvedSupplierId) return;
    (async () => {
      const [finRes, polRes] = await Promise.all([
        (supabase as any).rpc('get_supplier_payment_info', {
          _supplier_id: resolvedSupplierId,
          _context: cartPaymentContext,
        }),
        (supabase as any).from('supplier_policies').select('cancellation_policy').eq('supplier_id', resolvedSupplierId).maybeSingle(),
      ]);
      const finRow = Array.isArray(finRes.data) ? finRes.data[0] : finRes.data;
      if (finRow) {
        setSupplierPaymentInfo({
          methods: finRow.method_payments,
          installments: finRow.installments_client,
          pixDiscountEnabled: finRow.pix_discount_enabled ?? false,
          pixDiscountPercent: Number(finRow.pix_discount_percent ?? 0),
          deferredPixEnabled: finRow.deferred_pix_enabled ?? false,
          deferredPixDaysOptions: Array.isArray(finRow.deferred_pix_days_options) ? finRow.deferred_pix_days_options : [],
        });
      }
      if (polRes.data?.cancellation_policy) {
        const policy = typeof polRes.data.cancellation_policy === 'string'
          ? JSON.parse(polRes.data.cancellation_policy)
          : polRes.data.cancellation_policy;
        setSupplierCancellationPolicy(policy);
      }
    })();
  }, [resolvedSupplierId, cartPaymentContext]);


  const normalizedPaymentMethods = normalizeSupplierPaymentMethods(supplierPaymentInfo?.methods);
  const acceptsCreditCard = normalizedPaymentMethods.includes("credit_card");
  const acceptsPix = normalizedPaymentMethods.includes("pix");

  // Load supplier address for pickup
  useEffect(() => {
    if (!resolvedSupplierId) return;
    (async () => {
      console.log('🛒 [CartSheet] Loading address for supplier:', resolvedSupplierId);
      const { data, error } = await (supabase as any)
        .from('supplier_address')
        .select('*')
        .eq('supplier_id', resolvedSupplierId)
        .maybeSingle();
      
      if (error) {
        console.error('🛒 [CartSheet] Error loading supplier address:', error);
      }
      if (data) {
        console.log('🛒 [CartSheet] Supplier address loaded:', data);
        setSupplierAddress(data);
      }
    })();
  }, [resolvedSupplierId]);

  // Derive best event info from ANY cart item (not just first)
  const bestCartInfo = (() => {
    let cep = '';
    let date = '';
    let guests = '';
    let hour = '';
    let address = '';
    for (const item of cartItems) {
      if (!cep && item.cep) cep = item.cep;
      if (!date && item.date) date = item.date;
      if (!guests && item.guests && parseInt(item.guests) > 0) guests = item.guests;
      if (!hour && item.eventHourTime) hour = item.eventHourTime;
    }

    // Try to get address from sessionStorage if available
    try {
      const slug = supplierSlug || cartItems[0]?.supplierSlug;
      if (slug) {
        const stored = sessionStorage.getItem(`store_filters_${slug}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.address) address = parsed.address;
        }
      }
    } catch (e) {
      console.warn("Error reading address from storage:", e);
    }

    return { cep, date, guests, hour, address };
  })();

  const [eventCep, setEventCep] = useState(bestCartInfo.cep);
  const [eventAddress, setEventAddress] = useState(bestCartInfo.address);
  /** Endereço aberto resolvido via ViaCEP quando não temos o endereço salvo
   * (ex.: fluxo do cardápio selecionável não grava `address` no sessionStorage) —
   * evita mostrar só o número do CEP pro cliente. */
  const [resolvedCepAddress, setResolvedCepAddress] = useState('');
  useEffect(() => {
    const normalized = normalizeCEP(eventCep);
    if (eventAddress || normalized.length !== 8) return;
    let cancelled = false;
    fetch(`https://viacep.com.br/ws/${normalized}/json/`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || data?.erro) return;
        const parts = [data.logradouro, data.bairro, data.localidade && data.uf ? `${data.localidade}/${data.uf}` : data.localidade]
          .filter(Boolean);
        if (parts.length > 0) setResolvedCepAddress(parts.join(' - '));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventCep, eventAddress]);
  const [eventDate, setEventDate] = useState<Date | undefined>(
    bestCartInfo.date ? parseDateFromStorage(normalizeDate(bestCartInfo.date)) : undefined
  );
  const [eventGuests, setEventGuests] = useState(bestCartInfo.guests);
  const [eventHour, setEventHour] = useState(bestCartInfo.hour);

  /**
   * Categorias com preço especial (crianças, staff...). As quantidades são
   * globais (mesmas em todos os itens), por isso usamos o primeiro item que
   * as possui — somar item a item duplicaria a contagem.
   */
  const specialGuestsDisplay = useMemo(() => {
    const withSpecial = cartItems.find(
      (i: any) => i?.specialGuests && Object.keys(i.specialGuests).length > 0,
    ) as any;
    if (!withSpecial) return {};
    return buildSpecialGuestsDisplay(
      parseSpecialPricingTiers(withSpecial.specialPricingTiers),
      withSpecial.specialGuests,
    );
  }, [cartItems]);
  const specialGuestsCount = getSpecialGuestsCount(specialGuestsDisplay);
  const specialGuestsSuffix = formatSpecialGuestsSuffix(specialGuestsDisplay);


  // Sync event info from cart items when they change externally
  useEffect(() => {
    let cep = '';
    let date = '';
    let guests = '';
    let hour = '';
    let address = '';
    for (const item of cartItems) {
      if (!cep && item.cep) cep = item.cep;
      if (!date && item.date) date = item.date;
      if (!guests && item.guests && parseInt(item.guests) > 0) guests = item.guests;
      if (!hour && item.eventHourTime) hour = item.eventHourTime;
    }

    try {
      const slug = supplierSlug || cartItems[0]?.supplierSlug;
      if (slug) {
        const stored = sessionStorage.getItem(`store_filters_${slug}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.address) address = parsed.address;
        }
      }
    } catch (e) {}

    if (guests && guests !== eventGuests) setEventGuests(guests);
    if (cep && cep !== eventCep) setEventCep(cep);
    if (hour && hour !== eventHour) setEventHour(hour);
    if (address && address !== eventAddress) setEventAddress(address);
    if (date) {
      const newDate = parseDateFromStorage(normalizeDate(date));
      if (!eventDate || newDate.getTime() !== eventDate.getTime()) setEventDate(newDate);
    }
  }, [cartItems]);

  // Validate delivery when CEP changes
  useEffect(() => {
    const normalized = normalizeCEP(eventCep);
    if (normalized.length === 8 && supplierRanges.length > 0) {
      deliveryFetchRef.current = normalized;
      validateDeliveryWithRanges(normalized, supplierRanges, parseInt(bestCartInfo.guests) || 1);
    } else if (normalized.length < 8) {
      clearValidation();
    }
  }, [eventCep, supplierRanges, validateDeliveryWithRanges, clearValidation, bestCartInfo.guests]);

  // Re-read cart items from sessionStorage when simulator/storage updates them
  useEffect(() => {
    const handleFiltersUpdated = () => {
      setCartItems(getCartItems());
    };
    window.addEventListener('cart-filters-updated', handleFiltersUpdated);
    window.addEventListener('cart-updated', handleFiltersUpdated);
    return () => {
      window.removeEventListener('cart-filters-updated', handleFiltersUpdated);
      window.removeEventListener('cart-updated', handleFiltersUpdated);
    };
  }, [setCartItems]);

  // Completa o custo dos adicionais nos itens que chegam sem ele (rascunho/proposta/banco), para que mudar
  // convidados, data ou endereço no cabeçalho não tire os adicionais do total.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const enriched = await enrichCartItemsWithOptionalCosts(cartItems);
      if (cancelled || !enriched) return;
      try {
        sessionStorage.setItem('supplier_cart_items', JSON.stringify(enriched));
      } catch { /* sem sessionStorage */ }
      setCartItems(enriched);
    })();
    return () => { cancelled = true; };
  }, [cartItems]);

  // Tipo de evento trocado (ex.: Social → Corporativo): recalcula o carrinho na hora, sem sair e voltar.
  const recalcOnEventTypeRef = useRef<() => void>(() => {});
  useEffect(() => {
    const handler = () => recalcOnEventTypeRef.current();
    window.addEventListener(EVENT_TYPE_CHANGED, handler);
    return () => window.removeEventListener(EVENT_TYPE_CHANGED, handler);
  }, []);

  // Sync event info back to all cart items when changed, recalculating prices
  const updateAllItemsEventInfo = (cep?: string, date?: Date, guests?: string, sourceItems?: CartItem[]) => {
    const baseItems = sourceItems ?? cartItems;
    const updatedCep = cep ?? eventCep;
    const updatedDate = date ?? eventDate;
    const updatedGuests = guests ?? eventGuests;
    const newGuestCount = parseInt(updatedGuests) || 0;

    const updated = baseItems.map(item => {
      let newTotalPrice = item.totalPrice;
      let newPricePerPerson = item.pricePerPerson;

      // Item da proposta do fornecedor leva o valor que ele montou (extras, preço próprio): nunca recalcular pela
      // tabela base, senão o total cai (mesma regra da loja).
      // Recalculate price when guests change (works for both regular and selectable menus)
      if (guests !== undefined && newGuestCount > 0 && !item.fromSharedQuote) {
        try {
          const product = {
            id: item.productId,
            price: item.price,
            special_pricing: item.specialPricing,
            special_price: item.specialPrice,
            yearly_adjustments: item.yearlyAdjustments,
            seasonal_groups: item.seasonalGroups,
            event_type_adjustments: item.eventTypeAdjustments,
          };

          const itemEventType = resolveItemEventType(item);
          // A faixa de preço conta convidados + crianças/staff do item; o total cobra só os convidados.
          const itemSpecialTiers = parseSpecialPricingTiers(item.specialPricingTiers);
          const calculated = calculateProductPriceWithRanges(product, newGuestCount, undefined, item.date || null, itemEventType, getRangeGuestCount(newGuestCount, itemSpecialTiers, item.specialGuests));

          // Add substitution costs back after base recalculation
          const subsCostPerGuest = (item.substitutions
            ? Object.values(item.substitutions).reduce((sum, s) => sum + (s.additionalCost || 0), 0)
            : 0) + calculateAdditionsCost(normalizeAdditions(item.additions));
          // Os adicionais com preço escolhidos no cardápio também entram de novo no total.
          const subsTotal = subsCostPerGuest * newGuestCount * getItemEventTypeMultiplier(item, itemEventType) + getCartItemOptionalCost(item, newGuestCount, itemEventType);

          // Adicional das categorias com preço especial (crianças, staff...)
          const specialExtra = getSpecialExtraForProduct(product, newGuestCount, itemSpecialTiers, item.specialGuests, undefined, item.date || null, itemEventType);

          if (calculated > 0) {
            newTotalPrice = applyHourExtension(Math.max(calculated + subsTotal + specialExtra, 0), item.hourExtensionTiers, item.extraHours);
            newPricePerPerson = newTotalPrice / newGuestCount;

          } else if (item.pricePerPerson > 0) {
            // Fallback: use stored pricePerPerson × new guests
            newTotalPrice = applyHourExtension(
              (item.pricePerPerson * newGuestCount) + subsTotal,
              item.hourExtensionTiers,
              item.extraHours,
            );
            newPricePerPerson = newTotalPrice / newGuestCount;
          }

        } catch (err) {
          console.error('🛒 [CartSheet] Price recalculation error:', err);
        }
      }

      return {
        ...item,
        cep: updatedCep,
        date: updatedDate ? formatDateForStorage(updatedDate) : item.date,
        guests: updatedGuests || item.guests,
        totalPrice: newTotalPrice,
        pricePerPerson: newPricePerPerson,
      };
    });
    sessionStorage.setItem('supplier_cart_items', JSON.stringify(updated));
    setCartItems(updated);

    // Sync filters to store_filters_[slug] and general cache
    const slug = supplierSlug || cartItems[0]?.supplierSlug;
    let dateStr: string | null = null;
    if (updatedDate) {
      dateStr = formatDateForStorage(updatedDate);
    }
    if (slug) {
      try {
        sessionStorage.setItem(`store_filters_${slug}`, JSON.stringify({
          cep: updatedCep || '',
          date: dateStr,
          guests: updatedGuests || ''
        }));
      } catch {}
    }
    const cached = loadCacheData();
    saveCacheData({
      searchData: {
        selectedCategories: cached?.searchData?.selectedCategories ?? ['salgados'],
        guests: updatedGuests || '',
        budget: cached?.searchData?.budget ?? '0',
        date: updatedDate ? parseDateFromStorage(formatDateForStorage(updatedDate)) : cached?.searchData?.date,
        cep: (updatedCep || '').replace(/\D/g, '')
      }
    });

    // Notify other components (e.g. menu page) that filters were updated
    window.dispatchEvent(new Event('cart-filters-updated'));

    // Persist updated items to Supabase (parent + children)
    const newSubtotal = updated.reduce((s, i) => s + i.totalPrice, 0);
    onSyncCart?.(updated, { subtotal: newSubtotal, freight: deliveryFee, total: Math.max(0, newSubtotal + deliveryFee) });
  };
  recalcOnEventTypeRef.current = () => updateAllItemsEventInfo(undefined, undefined, eventGuests, getCartItems());


  // Inline edit mode for event info (CEP, date, guests)
  const [isEditingEventInfo, setIsEditingEventInfo] = useState(false);
  // Diferencia edição pedida pelo cliente (botão "Editar") da abertura automática
  // por falta de informação — só a automática se fecha sozinha.
  const [manualEditRequested, setManualEditRequested] = useState(false);


  /**
   * O cliente pode montar o carrinho sem ter informado CEP/data na loja.
   * Nesse caso não bloqueamos a adição dos itens: avisamos aqui e deixamos
   * ele completar as informações dentro do próprio carrinho.
   */
  const requiresCep = !cartItems.some((i: any) => i?.isPickup);
  const isMissingCep = requiresCep && normalizeCEP(eventCep).length !== 8;
  const isMissingDate = !eventDate;
  const hasMissingEventInfo = cartItems.length > 0 && (isMissingCep || isMissingDate);

  // Descrição completa do que está no carrinho (categorias, trocas, itens incluídos, adicionais com nome).
  const { items: contractedDetails, optionalNames: contractedOptionalNames } = useContractedItems(cartItems);

  // A data/CEP escolhidos no formulário do carrinho viviam só no estado da tela: o formulário se fecha
  // sozinho assim que tudo está preenchido (sem o cliente clicar em "Salvar informações") e os itens
  // seguiam sem data — o pedido era pago e criado sem data de entrega. Aqui, assim que o formulário
  // está completo e algum item ainda não tem a data/CEP, grava nos itens e no pedido.
  useEffect(() => {
    if (!eventDate || cartItems.length === 0) return;
    const cepOk = !requiresCep || normalizeCEP(eventCep).length === 8;
    if (!cepOk) return;
    const itemsLackInfo = cartItems.some((i: any) => !i.date || (requiresCep && !i.isPickup && normalizeCEP(i.cep || '').length !== 8));
    if (itemsLackInfo) updateAllItemsEventInfo(eventCep, eventDate, eventGuests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDate, eventCep, cartItems]);
  const missingInfoLabel = isMissingCep && isMissingDate
    ? 'o local da entrega e a data'
    : isMissingCep
      ? 'o local da entrega'
      : 'a data';

  // Abre automaticamente o formulário quando falta alguma informação e volta
  // para o resumo assim que tudo estiver preenchido.
  useEffect(() => {
    if (!open) return;
    if (hasMissingEventInfo) {
      setIsEditingEventInfo(true);
    } else if (!manualEditRequested) {
      setIsEditingEventInfo(false);
    }
  }, [open, hasMissingEventInfo, manualEditRequested]);

  // Ao fechar o carrinho, esquece o pedido manual de edição.
  useEffect(() => {
    if (!open) setManualEditRequested(false);
  }, [open]);


  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount_type: string;
    discount_value: number;
    calculated_discount: number;
  } | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  // O link da proposta é lido depois que a página abre: sem reler quando ele termina, o cupom do fornecedor não chegava ao
  // carrinho do cliente (o carrinho do cliente começa vazio, então nada mais disparava a leitura) e o total era gravado sem o desconto.
  const [sharedQuoteTick, setSharedQuoteTick] = useState(0);
  useEffect(() => {
    const bump = () => setSharedQuoteTick((t) => t + 1);
    window.addEventListener(SHARED_QUOTE_LOADED_EVENT, bump);
    return () => window.removeEventListener(SHARED_QUOTE_LOADED_EVENT, bump);
  }, []);

  // Load pre-applied discount from shared cart (supplier applied via SharedLinksTab)
  // OR from MGM referral link (?mgm=CODE)
  useEffect(() => {
    if (appliedCoupon) return; // Don't override if already applied
    try {
      // 1. Check shared cart discount
      const stored = sessionStorage.getItem('shared_cart_discount');
      if (stored) {
        const { code, value } = JSON.parse(stored);
        if (code && value > 0) {
          setAppliedCoupon({
            code,
            discount_type: 'fixed',
            discount_value: value,
            calculated_discount: value,
          });
          // A chave fica até o cliente remover o cupom: trocar de tela remonta o carrinho e o cupom não pode se perder.
          return;
        }
      }

      // 2. Check MGM referral discount
      const referralDiscount = sessionStorage.getItem('ameup_referral_discount');
      if (referralDiscount) {
        const percent = parseFloat(referralDiscount);
        if (percent > 0) {
          const currentSubtotal = cartItems.reduce((s, i) => s + i.totalPrice, 0);
          setAppliedCoupon({
            code: '🎁 Indicação',
            discount_type: 'percentage',
            discount_value: percent,
            calculated_discount: currentSubtotal * (percent / 100),
          });
        }
      }
    } catch {}
  }, [cartItems.length, sharedQuoteTick]);

  // Endereço fora das regiões atendidas (ou região suspensa): não deixa fechar a compra.
  const outOfArea = !cartItems.some((i: any) => i?.isPickup) && (validationResult?.isValid === false || validationResult?.isDisabled === true);

  const subtotal = cartItems.reduce((s, i) => s + i.totalPrice, 0);
  const discountAmount = appliedCoupon?.calculated_discount || 0;

  // Frete base vindo da validação de área (fonte da verdade do fornecedor)
  const baseDeliveryFee = (() => {
    if (cartItems.some(i => i.isPickup)) return 0;
    if (!validationResult?.isValid || !validationResult.area) return 0;
    return Number(validationResult.deliveryFee) || 0;
  })();

  // Pedágio estimado no servidor (Google Routes API + cache), somado ao frete.
  const [tollAmount, setTollAmount] = useState(0);
  // Pedágio ainda sendo consultado: o frete não é gravado até a resposta. Antes o pedido era gravado primeiro
  // sem pedágio (R$ 204,90) e depois com (R$ 245,50) — MAR-321A alternou entre os dois a cada abertura.
  const [tollPending, setTollPending] = useState(false);
  const tollCoords = validationResult?.coordinates;

  useEffect(() => {
    let cancelled = false;

    if (!resolvedSupplierId || baseDeliveryFee <= 0 || !tollCoords) {
      setTollAmount(0);
      setTollPending(false);
      return;
    }

    setTollPending(true);
    (async () => {
      try {
        const quote = await fetchTollQuote(
          resolvedSupplierId,
          { lat: tollCoords.lat, lng: tollCoords.lng },
          cartFreightContext,
        );
        if (!cancelled) setTollAmount(quote.amount);
      } finally {
        if (!cancelled) setTollPending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedSupplierId, baseDeliveryFee, tollCoords?.lat, tollCoords?.lng, cartFreightContext]);

  // Frete calculado (faixa/região + pedágio) e adicional que o fornecedor soma na proposta (mode=orçamento).
  // O cliente vê UM só frete: calculado + adicional. O adicional nunca substitui o frete calculado.
  const isPickupCart = cartItems.some(i => i.isPickup);
  // Cliente que abriu a proposta: o adicional também vem da cópia guardada ao carregar a proposta (o carrinho pode ser
  // remontado a partir dos itens e perder o campo). Quem monta a proposta só usa o que está nos itens.
  const [sharedFreightExtra, setSharedFreightExtra] = useState<number>(() => readSharedFreightExtra());
  useEffect(() => {
    const refresh = () => setSharedFreightExtra(readSharedFreightExtra());
    window.addEventListener('ameup-shared-freight-extra', refresh);
    refresh();
    return () => window.removeEventListener('ameup-shared-freight-extra', refresh);
  }, [open]);
  const isProposalBuilder = !!(isQuoteMode && onShareQuote);
  const freightExtra = outOfArea ? 0 : (cartFreightExtra(cartItems) || (isProposalBuilder ? 0 : sharedFreightExtra));
  const calculatedFreight = computeDeliveryFee({ base: baseDeliveryFee, toll: tollAmount, extra: 0, isPickup: isPickupCart });
  const deliveryFee = computeDeliveryFee({ base: baseDeliveryFee, toll: tollAmount, extra: freightExtra, isPickup: isPickupCart });

  // Item adicionado depois recebe o mesmo adicional (o valor vai em todos os itens do carrinho).
  useEffect(() => {
    const normalized = normalizeFreightExtra(cartItems);
    if (normalized !== cartItems) {
      sessionStorage.setItem('supplier_cart_items', JSON.stringify(normalized));
      setCartItems(normalized);
    }
  }, [cartItems]);

  // Frete extra da proposta: liga/desliga, valor e botão Salvar (só vale depois de salvar).
  const savedFreightExtraText = freightExtra > 0 ? String(freightExtra).replace('.', ',') : '';
  const [freightExtraOn, setFreightExtraOn] = useState<boolean>(freightExtra > 0);
  const [freightExtraDraft, setFreightExtraDraft] = useState<string>(savedFreightExtraText);
  useEffect(() => {
    setFreightExtraOn(freightExtra > 0);
    setFreightExtraDraft(freightExtra > 0 ? String(freightExtra).replace('.', ',') : '');
  }, [freightExtra]);
  const freightExtraDraftValue = parseFreightExtra(freightExtraDraft);
  const freightExtraDirty = freightExtraDraftValue !== freightExtra;
  const applyFreightExtra = (value: number) => {
    const updated = withFreightExtra(cartItems, value);
    sessionStorage.setItem('supplier_cart_items', JSON.stringify(updated));
    setCartItems(updated);
  };
  const saveFreightExtra = () => {
    if (freightExtraDraftValue <= 0) {
      toast.error('Informe um valor maior que zero para o frete extra.');
      return;
    }
    applyFreightExtra(freightExtraDraftValue);
    toast.success(`Frete extra de R$ ${formatCurrency(freightExtraDraftValue)} salvo. O cliente verá um único valor de frete.`);
  };
  const toggleFreightExtra = (on: boolean) => {
    setFreightExtraOn(on);
    if (on) return;
    setFreightExtraDraft('');
    if (freightExtra > 0 || freightExtraTouched(cartItems)) {
      applyFreightExtra(0);
      toast.success('Frete extra removido.');
    }
  };

  const total = Math.max(0, subtotal + deliveryFee - discountAmount);
  const today = new Date();
  today.setHours(0, 0, 0, 0);


  // Recalculate discount when subtotal changes
  useEffect(() => {
    if (appliedCoupon) {
      // Frete grátis: o desconto acompanha o frete vigente (muda com o CEP/região).
      const newDiscount = calculateCouponDiscount(appliedCoupon, { subtotal, freight: calculatedFreight });
      if (Math.abs(newDiscount - appliedCoupon.calculated_discount) > 0.001) {
        setAppliedCoupon(prev => prev ? { ...prev, calculated_discount: newDiscount } : null);
      }
    }
  }, [subtotal, calculatedFreight, appliedCoupon?.discount_type]);

  // Sync total_value (subtotal + freight - discount) to Supabase when key values change
  const syncRef = useRef<string>('');
  useEffect(() => {
    if (cartItems.length === 0) return;
    // Espera o pedágio: gravar antes deixava o pedido com o frete sem pedágio.
    if (tollPending) return;

    // Create a signature to avoid redundant syncs
    const signature = `${subtotal}-${deliveryFee}-${appliedCoupon?.code || ''}-${discountAmount}`;
    if (syncRef.current === signature) return;
    syncRef.current = signature;

    const timer = setTimeout(() => {
      onSyncCart?.(cartItems, {
        subtotal,
        freight: deliveryFee,
        total: Math.max(0, subtotal + deliveryFee - discountAmount),
        discount_code: appliedCoupon?.code,
        discount_value: discountAmount || undefined,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [deliveryFee, subtotal, appliedCoupon?.code, discountAmount, cartItems, tollPending]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      // Get supplier_id from cart items
      const supplierSlugFromCart = cartItems[0]?.supplierSlug;
      let supplierId: string | null = null;
      if (supplierSlugFromCart) {
        const { data: sup } = await (supabase as any)
          .from("suppliers_public")
          .select("id")
          .eq("slug", supplierSlugFromCart)
          .maybeSingle();
        supplierId = sup?.id || null;
      }

      const { data: coupon, error } = await supabase
        .from("discount_coupons")
        .select("*")
        .ilike("code", couponCode.trim())
        .eq("is_active", true)
        .maybeSingle();

      if (error || !coupon) {
        toast.error("Não encontramos esse cupom. Confira o código.");
        return;
      }
      if (supplierId && coupon.supplier_id !== supplierId) {
        toast.error("Esse cupom não vale para este fornecedor.");
        return;
      }
      if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
        toast.error("Esse cupom já expirou.");
        return;
      }
      if (coupon.max_uses && coupon.used_count >= coupon.max_uses) {
        toast.error("Esse cupom esgotou.");
        return;
      }
      if (coupon.min_order_value && subtotal < Number(coupon.min_order_value)) {
        toast.error(`Esse cupom vale para pedidos a partir de R$ ${Number(coupon.min_order_value).toFixed(2)}.`);
        return;
      }

      const calcDiscount = calculateCouponDiscount(coupon, { subtotal, freight: calculatedFreight });

      setAppliedCoupon({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: Number(coupon.discount_value),
        calculated_discount: calcDiscount,
      });
      setCouponCode("");
      toast.success(
        isShippingCouponType(coupon.discount_type)
          ? (deliveryFee > 0
              ? `Cupom ${coupon.code} aplicado: ${describeCoupon(coupon).toLowerCase()}!`
              : `Cupom ${coupon.code} aplicado: o desconto no frete vale assim que houver endereço de entrega.`)
          : `Cupom ${coupon.code} aplicado!`,
      );

      // Sync discount to Supabase immediately
      const newTotal = Math.max(0, subtotal - calcDiscount);
      onSyncCart?.(cartItems, { subtotal, freight: deliveryFee, total: Math.max(0, subtotal + deliveryFee - calcDiscount), discount_code: coupon.code, discount_value: calcDiscount });
    } catch {
      toast.error("Erro ao validar cupom");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    try { sessionStorage.removeItem('shared_cart_discount'); } catch { /* ignore */ }
    setAppliedCoupon(null);
    setCouponCode("");
    // Sync removal to Supabase immediately
    onSyncCart?.(cartItems, { subtotal, freight: deliveryFee, total: Math.max(0, subtotal + deliveryFee), discount_code: undefined, discount_value: 0 });
  };

  // Linha de seleção com UM item simples (sem escolha de sabores em combo): permite ajustar a quantidade no carrinho.
  const getQtyStepper = (item: CartItem) => {
    if (!item.isSelectableMenu || !item.selectedMenuItems) return null;
    const ids = Object.keys(item.selectedMenuItems);
    if (ids.length !== 1) return null;
    if (/\([^)]*,[^)]*\)/.test(item.itemName || '')) return null; // combo com sabores escolhidos: edita na página do item
    const id = ids[0];
    const qty = Number(item.selectedMenuItems[id]) || 1;
    const limits = qtyLimits[id] || { min: 1, max: 999 };
    return { id, qty, min: limits.min, max: limits.max };
  };

  const changeItemQty = (item: CartItem, newQty: number) => {
    const st = getQtyStepper(item);
    if (!st) return;
    if (newQty < st.min) {
      const updated = removeCartItem(item.productId);
      setCartItems(updated);
      onRemoveItem?.(item.productId);
      return;
    }
    if (newQty > st.max || newQty === st.qty) return;
    const unitTotal = item.totalPrice / st.qty;
    const unitPerPerson = item.pricePerPerson / st.qty;
    const round2 = (v: number) => Math.round(v * 100) / 100;
    const updatedItem: CartItem = {
      ...item,
      selectedMenuItems: { [st.id]: newQty },
      selectedItemsCount: newQty,
      totalPrice: round2(unitTotal * newQty),
      pricePerPerson: round2(unitPerPerson * newQty),
      itemName: (item.itemName || '').replace(/^\s*\d+\s*x/i, `${newQty}x`),
    };
    const updated = cartItems.map((i) => (i.productId === item.productId ? updatedItem : i));
    try { sessionStorage.setItem('supplier_cart_items', JSON.stringify(updated)); } catch { /* sem storage */ }
    setCartItems(updated);
    window.dispatchEvent(new Event('cart-updated'));
    const newSubtotal = updated.reduce((sum, i) => sum + i.totalPrice, 0);
    onSyncCart?.(updated, { subtotal: newSubtotal, freight: deliveryFee, total: Math.max(0, newSubtotal + deliveryFee) });
  };

  const cartTotalCount = cartItems.reduce((sum, it: any) => {
    if (it?.isSelectableMenu) {
      const map = it?.selectedMenuItems && typeof it.selectedMenuItems === 'object'
        ? Object.values(it.selectedMenuItems as Record<string, number>).reduce((q, v) => q + (Number(v) || 0), 0)
        : 0;
      return sum + (map || Number(it?.selectedItemsCount) || 1);
    }
    return sum + 1;
  }, 0);
  const cachedSupplier = loadSupplierCustomization();
  const mobileSupplierName = cartItems[0]?.supplierName || cachedSupplier?.supplierName || '';
  const mobileSupplierLogo = cachedSupplier?.logoUrl || '';
  const goBackToStore = () => {
    onOpenChange(false);
    if (supplierSlug) navigate(buildStoreReturnUrl(supplierSlug));
    else navigate(-1);
  };

  return (
    <>
    {/* Only one modal owns focus at a time; cancellation restores the cart. */}
    <Sheet open={open && !showSendFormat && !showNewQuotePrompt} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobileSheet ? "bottom" : "right"}
        className={isMobileSheet
          ? "h-auto max-h-[88dvh] rounded-t-3xl flex flex-col p-0 gap-0 overflow-x-hidden"
          : "w-[95vw] !max-w-[95vw] sm:!max-w-4xl lg:!max-w-5xl xl:!max-w-6xl flex flex-col p-0 gap-0 overflow-x-hidden"}
      >
        <SheetDescription className="sr-only">
          Carrinho de compras com itens, resumo do pedido e opções de checkout
        </SheetDescription>
        {/* Celular: topo da folha com a seta para baixo que recolhe o carrinho (volta para a página) */}
        {isMobileSheet && (
          <div className="flex shrink-0 items-center gap-3 px-4 pt-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Fechar carrinho"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground active:scale-95"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
            <span className="flex-1 text-center text-xs font-semibold uppercase tracking-wider">Carrinho</span>
            {cartItems.length > 0 ? (
              <button
                type="button"
                className="px-1 text-xs font-semibold"
                style={{ color: themeColor }}
                onClick={() => {
                  if (!window.confirm('Remover todos os itens do carrinho?')) return;
                  cartItems.forEach((it) => { removeCartItem(it.productId); onRemoveItem?.(it.productId); });
                  setCartItems([]);
                  onOpenChange(false);
                }}
              >
                Limpar
              </button>
            ) : (
              <span className="h-9 w-9" aria-hidden="true" />
            )}
          </div>
        )}
        {/* Celular: fornecedor + "Adicionar mais itens" (volta para a loja) */}
        {isMobileSheet && cartItems.length > 0 && (
          <div className="flex shrink-0 items-center gap-2.5 px-4 pt-3">
            {mobileSupplierLogo ? (
              <img src={mobileSupplierLogo} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold" style={{ backgroundColor: themeColor, color: themeTextColor }}>
                {(mobileSupplierName || '?').charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 leading-tight">
              {mobileSupplierName && <p className="truncate text-[13px] font-semibold">{mobileSupplierName}</p>}
              <button type="button" className="text-xs font-semibold" style={{ color: themeColor }} onClick={goBackToStore}>
                Adicionar mais itens
              </button>
            </div>
          </div>
        )}
        {/* Event Info Bar - compact styled summary */}
        {cartItems.length > 0 && (
          <div className="mx-3 my-2 sm:mx-4 sm:my-3 shrink-0 rounded-2xl bg-muted/5 border border-border overflow-hidden">
            {hasMissingEventInfo && !isMobileSheet && (
              <div className="flex items-start gap-2 px-3 py-2 sm:px-5 sm:py-3 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-300/70 text-amber-800 dark:text-amber-300">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-[11px] sm:text-xs leading-snug">
                  <span className="hidden sm:inline">Seus itens já estão no carrinho, mas ainda falta informar </span>
                  <span className="sm:hidden">Falta informar </span>
                  <strong>{missingInfoLabel}</strong>.
                  <span className="hidden sm:inline"> Preencha abaixo para continuar.</span>
                </p>
              </div>
            )}
            <div className="px-3 pt-2.5 pb-3 sm:px-5 sm:py-4 flex flex-col gap-2 sm:gap-3">
              {!isEditingEventInfo ? (
                <div className="flex flex-wrap gap-x-2 gap-y-1.5 text-[11px] sm:text-xs text-muted-foreground">
                  {(eventAddress || resolvedCepAddress || eventCep) && (
                    <span className="flex items-center gap-1 whitespace-nowrap bg-background px-2 py-0.5 rounded-md border border-border/50 shadow-sm font-medium text-foreground">
                      <MapPin className="w-3 h-3 text-muted-foreground" />
                      {eventAddress || resolvedCepAddress || `CEP: ${eventCep}`}
                    </span>
                  )}
                  {eventDate && (
                    <span className="flex items-center gap-1 whitespace-nowrap bg-background px-2 py-0.5 rounded-md border border-border/50 shadow-sm font-medium text-foreground">
                      <CalendarIcon className="w-3 h-3 text-muted-foreground" />
                      {format(eventDate, "dd/MM/yyyy")}
                    </span>
                  )}
                  {eventGuests && !isOrderOnlyCart && (
                    <span className="flex items-center gap-1 whitespace-nowrap bg-background px-2 py-0.5 rounded-md border border-border/50 shadow-sm font-medium text-foreground">
                      <Users className="w-3 h-3 text-muted-foreground" />
                      {(parseInt(eventGuests, 10) || 0) + specialGuestsCount} pessoas
                      {specialGuestsSuffix && (
                        <span className="font-normal text-muted-foreground">{specialGuestsSuffix}</span>
                      )}
                    </span>
                  )}

                  {eventHour && !isOrderOnlyCart && (
                    <span className="flex items-center gap-1 whitespace-nowrap bg-background px-2.5 py-1 rounded-md border border-border/50 shadow-sm font-medium text-foreground">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      {formatEventDuration((parseDurationToHours(eventHour) ?? 0) + (Number(cartItems.find((i) => i.eventHourTime)?.extraHours) || 0) || eventHour, cartItems.find((i) => i.eventHourTime)?.eventHourIsMax, { unit: 'h' }) || (eventHour.length > 5 ? eventHour.substring(0, 5) : eventHour)}
                    </span>
                  )}
                  {isMobileSheet && (
                    <button
                      type="button"
                      className="flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 text-[11px] font-semibold"
                      style={{ color: themeColor }}
                      onClick={() => {
                        setManualEditRequested(true);
                        setIsEditingEventInfo(true);
                      }}
                    >
                      <Pencil className="w-3 h-3" /> Editar
                    </button>
                  )}

                </div>
              ) : (
                <div className={cn("grid grid-cols-2 gap-2", isOrderOnlyCart ? "sm:grid-cols-2" : "sm:grid-cols-3")}>
                  <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {isOrderOnlyCart ? 'Endereço de Entrega' : 'Endereço do evento'}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      enterKeyHint="done"
                      autoComplete="postal-code"
                      value={formatCepForInput(eventCep)}
                      onChange={(e) => setEventCep(formatCepForInput(e.target.value))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          (e.currentTarget as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Digite o CEP"
                      maxLength={9}
                      className="h-10 sm:h-11 px-3 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground touch-manipulation min-w-0"
                    />

                  </label>
                  <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                    <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {isOrderOnlyCart ? 'Data de Entrega' : 'Data do evento'}</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className={cn("h-10 sm:h-11 px-3 rounded-md border border-input bg-background text-sm text-left touch-manipulation", eventDate ? "text-foreground" : "text-muted-foreground")}
                        >
                          {eventDate ? format(eventDate, "dd/MM/yyyy") : "Selecionar"}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={eventDate}
                          onSelect={(d) => setEventDate(d)}
                          disabled={(d) => d < today || (isDateDisabled ? isDateDisabled(d) : false)}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </label>
                  {!isOrderOnlyCart && (
                    <label className="col-span-2 sm:col-span-1 flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {guestsLabel || 'Convidados'}</span>
                      <input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        enterKeyHint="done"
                        value={eventGuests}
                        onChange={(e) => setEventGuests(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                        }}
                        className="h-10 sm:h-11 px-3 rounded-md border border-input bg-background text-base sm:text-sm text-foreground touch-manipulation"
                        placeholder="Ex: 50"
                      />
                    </label>
                  )}

                </div>
              )}

              {isEditingEventInfo ? (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="h-10 px-4"
                    onClick={() => {
                      const cepOk = !requiresCep || normalizeCEP(eventCep).length === 8;
                      if (!cepOk) {
                        toast.error('Informe um CEP válido para a entrega.');
                        return;
                      }
                      if (!eventDate) {
                        toast.error('Escolha a data do pedido.');
                        return;
                      }
                      updateAllItemsEventInfo(eventCep, eventDate, eventGuests);
                      setIsEditingEventInfo(false);
                      setManualEditRequested(false);
                      toast.success('Informações atualizadas!');
                    }}
                    style={{ backgroundColor: themeColor, color: themeTextColor }}
                  >
                    Salvar informações
                  </Button>
                  {!hasMissingEventInfo && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-9"
                      onClick={() => {
                        setIsEditingEventInfo(false);
                        setManualEditRequested(false);
                      }}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  className="max-md:hidden self-start text-[11px] leading-5 underline text-muted-foreground hover:text-foreground pb-0.5"
                  onClick={() => {
                    setManualEditRequested(true);
                    setIsEditingEventInfo(true);
                  }}
                >
                  {isOrderOnlyCart ? 'Editar local e data' : 'Editar local, data e convidados'}
                </button>

              )}

            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-4 pt-2 pb-2 sm:px-6 sm:pt-4 sm:pb-3">
          <div className="flex items-center justify-between">
            <SheetTitle className={isMobileSheet ? "sr-only" : "text-xl font-bold tracking-tight text-left"}>
              Seu Carrinho
            </SheetTitle>
            {(() => {
              const totalCount = cartItems.reduce((sum, it: any) => {
                if (it?.isSelectableMenu) {
                  const map = it?.selectedMenuItems && typeof it.selectedMenuItems === 'object'
                    ? Object.values(it.selectedMenuItems as Record<string, number>).reduce((s, q) => s + (Number(q) || 0), 0)
                    : 0;
                  return sum + (map || Number(it?.selectedItemsCount) || 1);
                }
                return sum + 1;
              }, 0);
              return (
                <span className="text-xs sm:text-sm text-muted-foreground">
                  {totalCount} {totalCount === 1 ? "item" : "itens"} no carrinho
                </span>
              );
            })()}
          </div>
          {cartItems.some(i => i.isPickup) && (
            <div className="mt-3 flex items-center gap-3 rounded-xl border-2 border-orange-500 bg-orange-50/60 dark:bg-orange-950/20 px-4 py-3 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <PackageCheck className="h-5 w-5" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold text-orange-700 dark:text-orange-400">Retirada no local</span>
                <span className="text-[11px] text-orange-600/90 dark:text-orange-400/80 leading-tight">
                  {supplierAddress && (supplierAddress.address_name || supplierAddress.city) ? (
                    <>
                      {supplierAddress.address_name && (
                        <span>{supplierAddress.address_name}{supplierAddress.address_number ? `, ${supplierAddress.address_number}` : ''}</span>
                      )}
                      {supplierAddress.address_complement && (
                        <span> - {supplierAddress.address_complement}</span>
                      )}
                      {(supplierAddress.address_name || supplierAddress.address_complement) && <br />}
                      {supplierAddress.city}{supplierAddress.state ? ` - ${supplierAddress.state}` : ''}
                    </>
                  ) : (
                    "Endereço do fornecedor"
                  )}
                </span>
                <span className="text-[11px] text-orange-600/90 dark:text-orange-400/80 leading-tight mt-1">
                  Sem entrega — você busca o pedido no endereço do fornecedor.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                <ShoppingCart className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium">Carrinho vazio</p>
              <p className="text-sm text-muted-foreground mt-1">Adicione itens do cardápio para começar</p>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
              {/* Product Cards + Summary */}
              <div className="flex flex-col xl:flex-row gap-6 xl:gap-8 overflow-x-hidden">
                {/* Left: Product items */}
                <div className="flex-1 min-w-0 space-y-4">
                  {cartItems.map((item) => {
                    const guestCount = parseInt(item.guests) || 0;
                    const qtyStepper = getQtyStepper(item);
                    return (
                      <div
                        key={item.productId}
                        className="rounded-xl border border-border bg-card overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => {
                          if (item.supplierSlug) {
                            const slug = item.productSlug || item.productId;
                            let url = `/cardapio/${item.supplierSlug}/${slug}`;
                            if (item.isSelectableMenu && item.selectedMenuItems && Object.keys(item.selectedMenuItems).length > 0) {
                              const selectedParam = Object.entries(item.selectedMenuItems)
                                .map(([id, qty]) => `${id}:${qty}`)
                                .join(",");
                              if (selectedParam) url += `?selected=${selectedParam}`;
                            }
                            if (sessionStorage.getItem('ameup_quote_mode') === 'true') {
                              const sep = url.includes('?') ? '&' : '?';
                              url += `${sep}mode=orcamento`;
                            }
                            onOpenChange(false);
                            navigate(url);
                          }
                        }}
                      >
                        <div className="flex flex-row">
                          {item.imageUrl ? (
                            <div className="w-16 sm:w-36 self-stretch bg-muted shrink-0">
                              <img
                                src={item.imageUrl}
                                alt={item.productName}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-16 sm:w-36 self-stretch bg-gradient-to-br from-muted to-muted/50 shrink-0 flex items-center justify-center">
                              <ShoppingCart className="h-8 w-8 text-muted-foreground/40" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0 p-2.5 sm:p-4 space-y-1 sm:space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5" style={{ backgroundColor: `${themeColor}15`, color: themeColor }}>
                                  {item.category === 'salgados' ? 'Buffet' : item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                                </span>
                                <h3 className="font-semibold text-[13px] sm:text-base leading-tight break-words">{item.productName}</h3>
                                {item.itemName && item.isSelectableMenu ? (
                                  (() => {
                                    // Sem "1x" e sem repetir o nome do produto: mostra só o que acrescenta (ex.: sabores do combo).
                                    const productName = item.productName.replace(/['"]/g, '').trim();
                                    const cleaned = item.itemName
                                      .split(' • ')
                                      .map((entry) => {
                                        let t = entry.replace(/^\s*\d+\s*x\s*/i, '').replace(/['"]/g, '').trim();
                                        if (t.toLowerCase().startsWith(productName.toLowerCase())) t = t.slice(productName.length).trim();
                                        return t.replace(/^\(/, '').replace(/\)$/, '').trim();
                                      })
                                      .filter(Boolean);
                                    if (cleaned.length === 0) return null;
                                    return (
                                      <div className="mt-1 space-y-0">
                                        {cleaned.map((entry, idx) => (
                                          <p key={idx} className="text-xs text-muted-foreground leading-relaxed">{entry}</p>
                                        ))}
                                      </div>
                                    );
                                  })()
                                ) : item.itemName && item.itemName.replace(/['"]/g, '').trim().toLowerCase() !== item.productName.replace(/['"]/g, '').trim().toLowerCase() ? (
                                  <p className="text-xs text-muted-foreground mt-0.5">{item.itemName}</p>
                                ) : null}
                                {(() => {
                                  const detail = contractedDetails.find((d) => d.productId === item.productId);
                                  return detail ? (
                                    <ContractedItemDetails item={detail} optionalNames={contractedOptionalNames} skipChoices collapsible={isMobileSheet} accent={themeColor} className="mt-2" />
                                  ) : null;
                                })()}
                                {item.eventHourTime && !isMobileSheet && (() => {
                                  const parts = item.eventHourTime!.split(':');
                                  const h = parseInt(parts[0]) || 0;
                                  const m = parseInt(parts[1]) || 0;
                                  if (h === 0 && m === 0) return null;
                                  const extra = Number(item.extraHours) || 0;
                                  const totalH = h + extra;
                                  return (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-xs text-muted-foreground">
                                        {item.eventHourIsMax ? 'até ' : ''}{m > 0 ? `${totalH}h${String(m).padStart(2, '0')}` : `${totalH}h`}
                                        {extra > 0 ? ` (+${extra}h adicional${extra > 1 ? 'is' : ''})` : ''}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                              {!qtyStepper && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 sm:h-8 sm:w-8 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updated = removeCartItem(item.productId);
                                  setCartItems(updated);
                                  onRemoveItem?.(item.productId);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              )}
                            </div>

                            <div className="flex items-center justify-between gap-2 sm:justify-end">
                              <p className={`sm:hidden text-[13px] font-semibold ${qtyStepper || item.isSelectableMenu ? '' : 'ml-auto'}`}>R$ {formatCurrency(item.totalPrice)}</p>
                              {qtyStepper && (
                                <div className="flex items-center gap-0.5 rounded-full border border-border px-1 py-0.5" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    aria-label={qtyStepper.qty <= qtyStepper.min ? 'Remover item' : 'Diminuir quantidade'}
                                    className="flex h-6 w-6 items-center justify-center rounded-full"
                                    style={{ color: themeColor }}
                                    onClick={(e) => { e.stopPropagation(); changeItemQty(item, qtyStepper.qty - 1); }}
                                  >
                                    {qtyStepper.qty <= qtyStepper.min ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                                  </button>
                                  <span className="min-w-5 text-center text-[13px] font-semibold">{qtyStepper.qty}</span>
                                  <button
                                    type="button"
                                    aria-label="Aumentar quantidade"
                                    className="flex h-6 w-6 items-center justify-center rounded-full disabled:opacity-35"
                                    style={{ color: themeColor }}
                                    disabled={qtyStepper.qty >= qtyStepper.max}
                                    onClick={(e) => { e.stopPropagation(); changeItemQty(item, qtyStepper.qty + 1); }}
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>

                            <Separator className="hidden sm:block" />

                            <div className="hidden sm:flex items-center justify-between">
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                SUBTOTAL
                              </span>
                              <span className="text-lg font-bold whitespace-nowrap">
                                R$ {formatCurrency(item.totalPrice)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right: Order Summary */}
                <div className="w-full xl:w-80 2xl:w-96 shrink-0 min-w-0">
                  <div className="rounded-xl border border-border bg-card p-3 sm:p-5 space-y-3 sm:space-y-4 sticky top-0">
                    <h3 className="font-semibold text-sm sm:text-base">{isProposalBuilder ? 'Resumo da proposta' : 'Resumo do Pedido'}</h3>

                     <div className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Subtotal ({(() => {
                            const totalCount = cartItems.reduce((sum, it: any) => {
                              if (it?.isSelectableMenu) {
                                const map = it?.selectedMenuItems && typeof it.selectedMenuItems === 'object'
                                  ? Object.values(it.selectedMenuItems as Record<string, number>).reduce((s, q) => s + (Number(q) || 0), 0)
                                  : 0;
                                return sum + (map || Number(it?.selectedItemsCount) || 1);
                              }
                              return sum + 1;
                            }, 0);
                            return `${totalCount} ${totalCount === 1 ? 'item' : 'itens'}`;
                          })()})
                        </span>
                        <span className="font-medium">R$ {formatCurrency(subtotal)}</span>
                      </div>
                      {/* Categorias com preço especial já estão somadas ao
                          subtotal; o detalhe aparece no cabeçalho do carrinho. */}

                      <div className="flex justify-between">
                        <span className="text-muted-foreground flex items-center gap-1">
                          {cartItems.some(i => i.isPickup) ? (
                            <>
                              <PackageCheck className="h-3.5 w-3.5 text-orange-600" />
                              Retirada no local
                            </>
                          ) : (
                            <>
                              <Truck className="h-3.5 w-3.5" />
                              Frete
                            </>
                          )}
                        </span>
                        {cartItems.some(i => i.isPickup) ? (
                          <span className="font-bold text-orange-600">Sem entrega</span>
                        ) : validationResult && validationResult.isValid === false ? (
                          <span className="font-medium text-destructive">Fora da área</span>
                        ) : deliveryFee > 0 ? (
                          <span className="font-medium">R$ {formatCurrency(deliveryFee)}</span>
                        ) : validationResult && validationResult.isValid && validationResult.isUnverified ? (
                          // Cobertura não confirmada (CEP sem localização precisa): não é frete grátis, o valor ainda
                          // precisa ser confirmado pelo fornecedor.
                          <span className="font-medium text-amber-600">A confirmar</span>
                        ) : validationResult && validationResult.isValid ? (
                          <span className="font-medium text-emerald-600">Grátis</span>
                        ) : (
                          <span className="font-medium text-muted-foreground">A calcular</span>
                        )}
                      </div>
                      {validationResult?.isUnverified && !cartItems.some(i => i.isPickup) && (
                        <p className="text-[11px] text-muted-foreground">
                          Não conseguimos confirmar o frete do seu endereço. O fornecedor confirma o valor com você.
                        </p>
                      )}
                      {tollAmount > 0 && !cartItems.some(i => i.isPickup) && (
                        <p className="text-[11px] text-muted-foreground">
                          Inclui R$ {formatCurrency(tollAmount)} de pedágio estimado no trajeto.
                        </p>
                      )}
                    </div>


                    {/* Cupom: o cliente vê o campo aqui; na proposta ele fica em "Opções da proposta" (só a linha de desconto fica no resumo). */}
                    {!isProposalBuilder && (
                    <div className="space-y-2">
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{appliedCoupon.code}</span>
                          </div>
                          <button onClick={handleRemoveCoupon} className="text-muted-foreground hover:text-destructive transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Código do cupom"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                            className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleApplyCoupon}
                            disabled={couponLoading || !couponCode.trim()}
                            className="h-9 px-3 text-xs"
                          >
                            {couponLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aplicar"}
                          </Button>
                        </div>
                      )}
                      {appliedCoupon && (
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600 font-medium">
                            Desconto ({describeCoupon(appliedCoupon)})
                          </span>
                          <span className="font-medium text-emerald-600">
                            -R$ {formatCurrency(appliedCoupon.calculated_discount)}
                          </span>
                        </div>
                      )}
                    </div>
                    )}
                    {isProposalBuilder && appliedCoupon && (
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-emerald-600 font-medium">Desconto ({describeCoupon(appliedCoupon)})</span>
                        <span className="font-medium text-emerald-600">-R$ {formatCurrency(appliedCoupon.calculated_discount)}</span>
                      </div>
                    )}

                    <Separator />

                    <div className="space-y-1">
                      {!isMobileSheet && (
                      <div className="flex justify-between items-baseline">
                        <span className="font-semibold">Total</span>
                        <span className="text-2xl font-bold">R$ {formatCurrency(total)}</span>
                      </div>
                      )}
                      <div className="text-xs text-muted-foreground text-right space-y-0.5">
                        {!isMobileSheet && acceptsCreditCard && (() => {
                          const raw = supplierPaymentInfo?.installments;
                          const maxInstallments = raw ? Math.max(...raw.match(/\d+/g)?.map(Number) || [1]) : 1;
                          return <p>ou {maxInstallments}x de R$ {formatCurrency(total / maxInstallments)}</p>;
                        })()}
                        {acceptsPix && supplierPaymentInfo?.pixDiscountEnabled && supplierPaymentInfo.pixDiscountPercent > 0 && (
                          <p className="text-emerald-600 font-medium">
                            Pix com {supplierPaymentInfo.pixDiscountPercent}% de desconto: R$ {formatCurrency(total * (1 - supplierPaymentInfo.pixDiscountPercent / 100))}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Como pagar: só as modalidades que o cliente pode usar para a data do evento (decididas no banco: get_checkout_payment_options) */}
                    {!isProposalBuilder && supplierPaymentInfo && normalizedPaymentMethods.length > 0 && (
                      <CartPaymentOptions supplierId={resolvedSupplierId} eventDate={bestCartInfo.date} context={cartPaymentContext} />
                    )}

                    {/* Opções da proposta: tudo que o fornecedor decide para ESTA proposta, numa caixa só (antes eram quatro caixas empilhadas). */}
                    {isProposalBuilder && (
                      <div className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border bg-background">
                        {!cartItems.some(i => i.isPickup) && !outOfArea && (
                          <div className="space-y-2 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <label htmlFor="freight-extra-toggle" className="text-sm font-medium text-foreground">
                                Frete extra
                              </label>
                              <Switch id="freight-extra-toggle" checked={freightExtraOn} onCheckedChange={toggleFreightExtra} />
                            </div>
                            {freightExtraOn && (
                              <div className="space-y-2">
                                <p className="text-[11px] text-muted-foreground">Soma ao frete calculado. O cliente vê um único valor de frete.</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">R$</span>
                                  <input
                                    id="freight-extra"
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0,00"
                                    aria-label="Valor do frete extra"
                                    value={freightExtraDraft}
                                    onChange={(e) => setFreightExtraDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveFreightExtra(); } }}
                                    className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-right text-sm"
                                  />
                                  <Button type="button" size="sm" className="h-9 shrink-0" disabled={!freightExtraDirty || freightExtraDraftValue <= 0} onClick={saveFreightExtra}>
                                    Salvar
                                  </Button>
                                </div>
                                {freightExtra > 0 && !freightExtraDirty ? (
                                  <p className="text-[11px] font-medium text-emerald-600">
                                    Frete extra de R$ {formatCurrency(freightExtra)} incluído no frete desta proposta.
                                  </p>
                                ) : (
                                  <p className="text-[11px] text-amber-600">Clique em Salvar para incluir este valor na proposta.</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {cartHasChoiceCategories(cartItems) && (
                          <div className="flex items-center justify-between gap-3 p-3">
                            <div className="min-w-0">
                              <Label htmlFor="quote-only-selected" className="text-sm font-medium text-foreground">
                                Mostrar só o que você escolheu
                              </Label>
                              <p className="text-[11px] text-muted-foreground">
                                Os itens que ficaram de fora não aparecem para o cliente.
                              </p>
                            </div>
                            <Switch
                              id="quote-only-selected"
                              checked={onlySelected}
                              onCheckedChange={(checked) => {
                                setOnlySelected(checked);
                                setQuoteOnlySelected(checked);
                              }}
                            />
                          </div>
                        )}

                        {/* Pagamento pós-evento: o fornecedor escolhe, pra ESTA proposta, um dos prazos habilitados em Finanças. */}
                        {supplierPaymentInfo && shouldOfferDeferredPix({ isQuoteMode, deferredPixEnabled: supplierPaymentInfo.deferredPixEnabled, daysOptions: supplierPaymentInfo.deferredPixDaysOptions }) && (
                          <div className="space-y-2 p-3">
                            <p className="text-sm font-medium text-foreground">Pagamento pós-evento</p>
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                type="button"
                                onClick={() => onDeferredPixDaysChange?.(null)}
                                className={cn(
                                  "px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors",
                                  !deferredPixDays ? "bg-background border-border text-foreground" : "border-transparent text-muted-foreground hover:bg-background/60"
                                )}
                              >
                                Não oferecer
                              </button>
                              {supplierPaymentInfo.deferredPixDaysOptions.slice().sort((a, b) => a - b).map((days) => (
                                <button
                                  key={days}
                                  type="button"
                                  onClick={() => onDeferredPixDaysChange?.(days)}
                                  className={cn(
                                    "px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors",
                                    deferredPixDays === days ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-foreground hover:bg-muted"
                                  )}
                                >
                                  {days} dias após o evento
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="p-3">
                          {appliedCoupon ? (
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                                <Tag className="h-4 w-4 text-emerald-600" />
                                {appliedCoupon.code}
                              </span>
                              <button onClick={handleRemoveCoupon} aria-label="Remover cupom" className="text-muted-foreground hover:text-destructive transition-colors">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : showCouponField ? (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                autoFocus
                                placeholder="Código do cupom"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                                className="flex-1 h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                              />
                              <Button variant="outline" size="sm" onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()} className="h-9 px-3 text-xs">
                                {couponLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aplicar"}
                              </Button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setShowCouponField(true)}
                              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <Tag className="h-4 w-4" />
                              Adicionar cupom
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {isQuoteMode && onShareQuote && (
                      <div className="flex flex-col gap-2">
                        <Button
                          className="w-full h-12 text-base font-semibold gap-2 rounded-lg bg-[#25D366] hover:bg-[#20BA5A] text-white"
                          size="lg"
                          onClick={() => { if (!isSharing) setShowSendFormat(true); }}
                          disabled={isLoading || isSharing}
                        >
                          {sharingAction === 'whatsapp' ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Gerando link...
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .018 5.393 0 12.03a11.854 11.854 0 001.592 5.955L0 24l6.117-1.605a11.803 11.803 0 005.925 1.586h.005c6.635 0 12.032-5.396 12.036-12.031a11.816 11.816 0 00-3.53-8.503" />
                              </svg>
                              Enviar para o WhatsApp
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full h-12 text-base font-semibold gap-2 rounded-lg border-2"
                          size="lg"
                          onClick={async () => {
                            if (isSharing) return;
                            setSharingAction('copy');
                            try {
                              await onSyncCart?.(cartItems, {
                                subtotal,
                                freight: deliveryFee,
                                total,
                                discount_code: appliedCoupon?.code,
                                discount_value: discountAmount || undefined,
                              });
                              const shared = await onShareQuote('copy');
                              if (shared === true) setShowNewQuotePrompt(true);
                            } catch (err) {
                              console.error('Copy quote error:', err);
                            } finally {
                              setSharingAction(null);
                            }
                          }}
                          style={{
                            borderColor: themeColor,
                            color: themeColor,
                          }}
                          disabled={isLoading || isSharing}
                        >
                          {sharingAction === 'copy' ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Copiando...
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Copiar Proposta
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    <button
                      className="max-md:hidden w-full text-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                      onClick={() => {
                        onOpenChange(false);
                        if (supplierSlug) {
                          navigate(buildStoreReturnUrl(supplierSlug));
                        } else {
                          navigate(-1);
                        }
                      }}
                    >
                      Continuar comprando
                    </button>
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Rodapé fixo: total + CTA sempre visíveis, sem precisar rolar */}
        {cartItems.length > 0 && !(isQuoteMode && onShareQuote) && (
          <div className="sticky bottom-0 shrink-0 border-t border-border bg-background/95 backdrop-blur px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-2">
            {outOfArea && (
              <p className="text-sm text-destructive font-medium text-center">😔 Ainda não atendemos esta região</p>
            )}
            {hasMissingEventInfo && !isMobileSheet && (
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                Falta informar {missingInfoLabel} para fechar a compra.
              </p>
            )}
            <div className="flex items-center gap-3">
            {isMobileSheet && (
              <div className="shrink-0 leading-tight">
                <p className="text-[11px] text-muted-foreground">Total com a entrega</p>
                <p className="text-base font-bold">
                  R$ {formatCurrency(total)} <span className="text-[11px] font-normal text-muted-foreground">/ {cartTotalCount} {cartTotalCount === 1 ? 'item' : 'itens'}</span>
                </p>
              </div>
            )}
            <Button
              type="button"
              className="flex-1 md:w-full h-11 sm:h-12 text-sm font-semibold gap-2 rounded-lg touch-manipulation"
              size="lg"
              onClick={async () => {
                if (isLoading || isCheckingOut) return;
                if (outOfArea) {
                  toast.error('😔 Ainda não atendemos esta região');
                  return;
                }
                if (hasMissingEventInfo) {
                  setIsEditingEventInfo(true);
                  toast.error(`Informe ${missingInfoLabel} para continuar.`);
                  return;
                }
                setIsCheckingOut(true);
                try {
                  // Garante que a data/CEP do formulário estejam nos itens antes de gravar (ver efeito acima).
                  let itemsToSync = cartItems;
                  if (eventDate && cartItems.some((i: any) => !i.date)) {
                    itemsToSync = cartItems.map((i: any) => ({
                      ...i,
                      date: i.date || formatDateForStorage(eventDate),
                      cep: i.cep || eventCep,
                    }));
                    sessionStorage.setItem('supplier_cart_items', JSON.stringify(itemsToSync));
                    setCartItems(itemsToSync);
                  }
                  // AWAIT sync so the cart (menu + totals) is persisted to
                  // the parent event before checkout flow overwrites status.
                  await onSyncCart?.(itemsToSync, { subtotal, freight: deliveryFee, total, discount_code: appliedCoupon?.code, discount_value: discountAmount || undefined });
                  onOpenChange(false);
                  onCheckout();
                } finally {
                  setIsCheckingOut(false);
                }
              }}
              disabled={isLoading || isCheckingOut || outOfArea || tollPending}
              style={{ backgroundColor: themeColor, color: themeTextColor }}
            >
              {(isLoading || isCheckingOut) ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  Fechar a Compra
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>

    <QuoteSendFormatModal
      open={showSendFormat}
      onOpenChange={setShowSendFormat}
      busy={sharingAction === 'whatsapp'}
      onConfirm={async (format) => {
        if (sendInFlight.current || !onShareQuote) return;
        sendInFlight.current = true;
        setSharingAction('whatsapp');
        try {
          await onSyncCart?.(cartItems, {
            subtotal, freight: deliveryFee, total,
            discount_code: appliedCoupon?.code,
            discount_value: discountAmount || undefined,
          });
          const shared = await onShareQuote('whatsapp', format);
          if (shared === true) {
            setShowSendFormat(false);
            setShowNewQuotePrompt(true);
          }
        } catch (err) {
          console.error('Share quote error:', err);
          toast.error('Não foi possível enviar a proposta. Tente novamente.');
        } finally {
          sendInFlight.current = false;
          setSharingAction(null);
        }
      }}
    />
    <AlertDialog open={showNewQuotePrompt} onOpenChange={setShowNewQuotePrompt}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Proposta compartilhada!</AlertDialogTitle>
          <AlertDialogDescription>
            Deseja gerar uma nova proposta? Vamos limpar o carrinho e começar uma proposta do zero,
            sem alterar a que você acabou de enviar.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Não, continuar nesta</AlertDialogCancel>
          <AlertDialogAction onClick={startNewQuote}>Sim, gerar nova proposta</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default CartSheet;
